import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AlertCircle, FileText, Shield, DollarSign, Package, Wrench, Car, Scale } from 'lucide-react';

const TermsAndConditions = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const sections = [
    { id: 'payment', title: 'Payment Terms', icon: DollarSign },
    { id: 'delivery', title: 'Delivery & Shipping', icon: Package },
    { id: 'warranty', title: 'Warranty', icon: Shield },
    { id: 'returns', title: 'Returns Policy', icon: Package },
    { id: 'service', title: 'Service Policies', icon: Wrench },
    { id: 'vehicle', title: 'Vehicle Sales', icon: Car },
    { id: 'general', title: 'General Provisions', icon: Scale },
  ];

  return (
    <>
      <Helmet>
        <title>Terms and Conditions | Paradise-Performance Auto Repair & Sales</title>
        <meta name="description" content="Terms and conditions for auto repair services, parts sales, and vehicle purchases. Serving Paradise, TX and the Dallas-Fort Worth area." />
        <link rel="canonical" href="https://paradise-performance.com/terms-and-conditions" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-900 to-gray-800 text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms and Conditions</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Please review our terms of service for auto repair, parts sales, and vehicle purchases
            </p>
            <p className="text-sm text-gray-400 mt-4">Effective Date: November 13, 2024</p>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-gray-100 py-8 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg hover:bg-gray-50 transition"
                >
                  <Icon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium">{section.title}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          
          {/* Payment Terms */}
          <motion.div {...fadeInUp} id="payment" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <DollarSign className="w-8 h-8 text-green-600 mr-3" />
              1. Payment Terms
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">1.1 Payment Due Dates</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Service & Repair Work:</strong> Payment due upon completion unless prior credit arrangements approved</li>
                  <li>• <strong>Vehicle Purchases:</strong> Per purchase agreement terms</li>
                  <li>• <strong>Parts Orders:</strong> Due on the 10th of the month following purchase</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">1.2 Late Payment Charges</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• 1.5% monthly service charge (18% APR) on accounts overdue 30+ days</li>
                  <li>• Storage fees of $25/day after 7 days of completion notification</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">1.3 Price Changes</h3>
                <p className="text-gray-700">
                  Due to market conditions and parts availability, prices subject to change without notice. 
                  Written estimates valid for 30 days.
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold mb-2">1.4 Collection & Legal</h3>
                    <p className="text-gray-700 text-sm">
                      Unpaid accounts subject to collection. Prevailing party entitled to attorney's fees. 
                      Disputes resolved in Wise County, Texas courts under Texas law. 
                      We reserve the right to exercise mechanic's lien for unpaid services.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Delivery & Shipping */}
          <motion.div {...fadeInUp} id="delivery" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Package className="w-8 h-8 text-blue-600 mr-3" />
              2. Delivery & Shipping
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">2.1 Loss or Damage in Transit</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Title and risk pass to customer upon carrier delivery (FOB our location)</li>
                  <li>• We assist with carrier claims for lost/damaged shipments</li>
                  <li>• Document damage immediately on carrier receipt</li>
                  <li>• Notify us in writing within 48 hours</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">2.2 Order Errors</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Report wrong parts or quantities immediately</li>
                  <li>• We investigate claims within 3 business days</li>
                  <li>• Claims must be made within 7 days of receipt</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Warranty */}
          <motion.div {...fadeInUp} id="warranty" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Shield className="w-8 h-8 text-green-600 mr-3" />
              3. Warranty Disclaimers
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <p className="font-bold text-red-800 mb-2">3.1 Parts Warranty Disclaimer</p>
                <p className="text-sm text-gray-700">
                  PARADISE PERFORMANCE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY 
                  AND FITNESS FOR PARTICULAR PURPOSE. MANUFACTURER WARRANTIES APPLY WHERE APPLICABLE.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">3.2 Service Warranty</h3>
                <p className="text-gray-700 mb-3">
                  <strong>12-month/12,000-mile warranty on labor</strong> (whichever comes first)
                </p>
                <p className="text-gray-700 mb-2">Warranty VOID if:</p>
                <ul className="space-y-1 text-gray-700 ml-4">
                  <li>• Vehicle modified after service</li>
                  <li>• Used for racing/competition</li>
                  <li>• Improper maintenance</li>
                  <li>• Other shops attempt repairs</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">3.3 Vehicle Sales Warranty</h3>
                <p className="text-gray-700">
                  Used vehicles sold "AS IS" unless written warranty provided. 
                  Texas Lemon Law may apply - ask for details.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Returns Policy */}
          <motion.div {...fadeInUp} id="returns" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Package className="w-8 h-8 text-orange-600 mr-3" />
              4. Merchandise Returns
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-4">
                <h3 className="text-lg font-semibold text-red-800 mb-2">4.1 Non-Returnable Items</h3>
                <ul className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                  <li>• Electrical/electronic parts</li>
                  <li>• Special order items</li>
                  <li>• Custom/modified parts</li>
                  <li>• Opened fluids/chemicals</li>
                  <li>• Fuel system components</li>
                  <li>• Used parts</li>
                  <li>• Installed parts</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">4.2 Return Time Limits</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• 30-day return window from purchase</li>
                  <li>• 20% minimum restocking fee</li>
                  <li>• NO returns after 30 days</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">4.3 Return Requirements</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Prior approval & RMA number required</li>
                  <li>• Original packaging, never installed</li>
                  <li>• Clean, resaleable condition</li>
                  <li>• Customer pays return shipping</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">4.4 Core Returns</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• 30-day return window for full credit</li>
                  <li>• Must be identical to part purchased</li>
                  <li>• Clean and in original packaging</li>
                  <li>• Credit may take 4-6 weeks</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Service Policies */}
          <motion.div {...fadeInUp} id="service" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Wrench className="w-8 h-8 text-purple-600 mr-3" />
              5. Service Policies
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">5.1 Estimates & Authorization</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Written estimates upon request</li>
                  <li>• Diagnostic fees credited toward repair if authorized</li>
                  <li>• All work requires authorization</li>
                  <li>• Additional repairs need separate approval</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">5.2 Parts Usage</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• We use OEM or quality aftermarket parts</li>
                  <li>• Customer parts installed at owner risk</li>
                  <li>• No labor warranty on customer parts</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">5.3 Vehicle Storage</h3>
                <p className="text-gray-700">
                  Vehicles not picked up within 7 days: <strong>$25/day storage fee</strong><br/>
                  Abandoned over 30 days may be sold per Texas law
                </p>
              </div>
            </div>
          </motion.div>

          {/* Vehicle Sales */}
          <motion.div {...fadeInUp} id="vehicle" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Car className="w-8 h-8 text-indigo-600 mr-3" />
              6. Vehicle Sales Terms
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">6.1 Deposits</h3>
                <p className="text-gray-700">
                  Non-refundable unless we cannot deliver as described. 
                  Holds vehicle for 7 days maximum.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">6.2 Trade-Ins</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Values valid 7 days</li>
                  <li>• Subject to final inspection</li>
                  <li>• Clear title required</li>
                </ul>
              </div>

            </div>
          </motion.div>

          {/* General Provisions */}
          <motion.div {...fadeInUp} id="general" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Scale className="w-8 h-8 text-gray-600 mr-3" />
              7. General Provisions
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">7.1 Limitation of Liability</h3>
                <p className="text-gray-700">
                  Our liability limited to repair/replacement cost. No consequential or incidental damages. 
                  No liability for loss of use or time.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">7.2 Jurisdiction</h3>
                <p className="text-gray-700">
                  Services performed at 305 Co Rd 3470, Paradise, TX 76073. 
                  Disputes resolved in Wise County, Texas courts under Texas law.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">7.3 Agreement</h3>
                <p className="text-gray-700">
                  These terms constitute entire agreement. No verbal agreements binding. 
                  We reserve right to modify terms at any point.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div {...fadeInUp} className="bg-gray-800 text-white rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Contact Paradise-Performance</h2>
            <div className="space-y-2">
              <p>305 Co Rd 3470, Paradise, TX 76073</p>
              <p>
                <a href="tel:8173822316" className="text-red-400 hover:text-red-300">
                  (817) 382-2316
                </a>
              </p>
              <p>
                <a href="mailto:sales@paradise-performance.com" className="text-red-400 hover:text-red-300">
                  info@paradise-performance.com
                </a>
              </p>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                By using our services, you acknowledge and agree to these terms and conditions.
              </p>
              <Link 
                to="/contact" 
                className="inline-block mt-4 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg font-semibold transition"
              >
                Contact Us With Questions
              </Link>
            </div>
          </motion.div>

        </div>
      </section>
    </>
  );
};

export default TermsAndConditions;
